'use client';

/**
 * ObeyaPlanPositionRoomsCard — Plan Position's room table: one row
 * per room type, ranked by that room's single widest undecided
 * requirement spread. Replaces Room Story's twelve full-width
 * narrative beats with rows; click a row to expand it IN PLACE —
 * tier totals, every undecided requirement by spread, and the room's
 * own room_story narrative — instead of routing to the rail. The
 * rail's per-tab insights aren't scoped per preset (resolved once per
 * `sel:mcp`, shared across every mcp tab), so a rail detail card here
 * would either leak onto unrelated tabs or need self-suppression
 * logic that hides itself based on which tab is active — a card
 * papering over a compositor gap instead of the compositor actually
 * supporting it. Inline expansion sidesteps that entirely: every
 * field shown here is already in this card's own room_story fetch,
 * so no new card, no rail, no backend change.
 *
 * The ranking (and the undecided list) is computed HERE, client-side,
 * from room_story's own per-requirement `candidate_price_spread`
 * facts (spread_type == 'undecided' only — a decided line's spread is
 * a reopen lever, not an undecided one, same distinction
 * room_story.py's docstring draws between the two). Nothing here is a
 * number the BFF didn't already ship; this component only sorts,
 * filters and picks the max.
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — ObeyaPlanPositionRoomsCard
 * @see klyde_edge/klyde_intelligence/projects/room_story.py
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo, useState } from 'react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';

interface TierTotals {
  budget: number | null; mid: number | null; premium: number | null;
  resolved_requirements: number; unresolved_requirements: number;
}
interface RoomRequirement {
  requirement: string;
  candidate_price_spread: { min: number; max: number } | null;
  spread_type: 'undecided' | 'reopen' | null;
}
interface RoomStoryRow {
  key: string; label: string;
  vendor_count: number;
  requirements_total: number; requirements_selected: number; candidates_total: number;
  tier_totals: TierTotals;
  requirements: RoomRequirement[];
  insight: string;
}
interface RoomStoryData { rooms: RoomStoryRow[] }

const money = (v: number) => `$${Math.round(v).toLocaleString()}`;

/** Widest undecided spread in a room — the real lever, per room_story.py's
 *  own distinction between 'undecided' (moves the moment a first choice is
 *  made) and 'reopen' (moves only on revisiting a settled pick). */
function widestUndecided(room: RoomStoryRow): RoomRequirement | null {
  let best: RoomRequirement | null = null;
  let bestSpread = -1;
  for (const r of room.requirements) {
    if (r.spread_type !== 'undecided' || !r.candidate_price_spread) continue;
    const spread = r.candidate_price_spread.max - r.candidate_price_spread.min;
    if (spread > bestSpread) { bestSpread = spread; best = r; }
  }
  return best;
}

/** Every undecided requirement, by spread, widest first. Capped display
 *  (top 4 + "+N more") happens in the render — this returns the full list
 *  so the count is always real. */
function undecidedByspread(room: RoomStoryRow): { req: string; spread: number }[] {
  return room.requirements
    .filter((r): r is RoomRequirement & { candidate_price_spread: { min: number; max: number } } =>
      r.spread_type === 'undecided' && !!r.candidate_price_spread)
    .map((r) => ({ req: r.requirement, spread: r.candidate_price_spread.max - r.candidate_price_spread.min }))
    .sort((a, b) => b.spread - a.spread);
}

const UNDECIDED_PREVIEW_COUNT = 4;

export function ObeyaPlanPositionRoomsCard() {
  const { mcpId } = useViewportSelection();
  const { contentKey } = useCardIdentity();
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const strategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'room_story'));
  const { data, isLoading } = useStrategyData<RoomStoryData>(
    strategy, { semantic_id: mcpId ?? '' }, !!mcpId && !!strategy,
  );
  const rooms = data?.data?.rooms ?? [];

  const ranked = useMemo(() => rooms
    .map((room) => ({ room, widest: widestUndecided(room) }))
    .sort((a, b) => {
      const aSpread = a.widest ? a.widest.candidate_price_spread!.max - a.widest.candidate_price_spread!.min : -1;
      const bSpread = b.widest ? b.widest.candidate_price_spread!.max - b.widest.candidate_price_spread!.min : -1;
      return bSpread - aSpread;
    }), [rooms]);

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_plan_position_rooms',
    status: (data ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: strategy ? ['strategy-data', strategy, { semantic_id: mcpId ?? '' }] : null,
    text: `Rooms, ranked by widest undecided spread: ${ranked.length} room types`,
    // Mirrors what's actually painted, not a filtered summary: a
    // collapsed row only paints key/label/decided/widest_undecided, so
    // that's all its entry carries; the expanded row's entry adds
    // exactly what the expansion renders — tier totals (painted, same
    // money() formatting), the capped undecided list (top
    // UNDECIDED_PREVIEW_COUNT, same as the row shows), and the
    // insight text — never the full unrendered list.
    json: ranked.map(({ room, widest }) => {
      const expanded = expandedKey === room.key;
      const base = {
        key: room.key, label: room.label,
        decided: `${room.requirements_selected} of ${room.requirements_total}`,
        widest_undecided: widest ? { requirement: widest.requirement, spread: money(widest.candidate_price_spread!.max - widest.candidate_price_spread!.min) } : null,
        expanded,
      };
      if (!expanded) return base;
      const undecided = undecidedByspread(room);
      return {
        ...base,
        tier_totals: room.tier_totals.budget != null ? {
          budget: money(room.tier_totals.budget),
          mid: money(room.tier_totals.mid!),
          premium: money(room.tier_totals.premium!),
        } : null,
        undecided: undecided.slice(0, UNDECIDED_PREVIEW_COUNT).map((u) => ({ requirement: u.req, spread: money(u.spread) })),
        undecided_more_count: Math.max(0, undecided.length - UNDECIDED_PREVIEW_COUNT),
        insight: room.insight || null,
      };
    }),
    state: { expanded_key: expandedKey },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, data, mcpId, strategy, ranked, expandedKey]));

  if (!mcpId) return null;

  return (
    <div className="flex flex-col h-full p-6 gap-3 overflow-auto">
      <div className="flex items-baseline gap-2">
        <span className={cn(text.eyebrow)}>By room</span>
        <span className={cn(text.muted, 'text-[11px] tabular-nums')}>
          {rooms.length} room types · {rooms.reduce((a, r) => a + r.requirements_total, 0)} requirements ·{' '}
          {rooms.reduce((a, r) => a + r.requirements_selected, 0)} decided
        </span>
        <span className="flex-1" />
        <span className={cn(text.muted, 'text-[11px]')}>ranked by widest undecided spread — click a room to expand its read</span>
      </div>

      {isLoading && rooms.length === 0 ? (
        <div className={cn(text.muted, 'mt-6 text-center')}>Generating…</div>
      ) : rooms.length === 0 ? (
        <div className={cn(text.muted, 'mt-6 text-center')}>No rooms with placements yet</div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden bg-card">
          {ranked.map(({ room, widest }, i) => {
            const pct = room.requirements_total > 0 ? Math.round((room.requirements_selected / room.requirements_total) * 100) : 0;
            const spread = widest ? widest.candidate_price_spread!.max - widest.candidate_price_spread!.min : null;
            const expanded = expandedKey === room.key;
            const undecided = expanded ? undecidedByspread(room) : [];
            return (
              <div key={room.key} className={i > 0 ? 'border-t border-border' : undefined}>
                <button
                  onClick={() => setExpandedKey(expanded ? null : room.key)}
                  className={cn(
                    'w-full flex items-center gap-4 px-4 py-3 text-left',
                    expanded ? 'bg-primary/10 shadow-[inset_2px_0_0_var(--primary)]' : 'hover:bg-muted/30',
                  )}
                >
                  <span className="w-[150px] flex-none">
                    <span className="block text-[12.5px] font-semibold text-foreground">{room.label}</span>
                    <span className={cn(text.muted, 'block text-[10.5px] tabular-nums')}>
                      {room.candidates_total} candidates · {room.vendor_count} vendors
                    </span>
                  </span>
                  <span className="w-[60px] flex-none">
                    <span className="block h-1 rounded bg-muted/40 overflow-hidden mb-1">
                      <span className="block h-full bg-primary" style={{ width: `${pct}%` }} />
                    </span>
                    <span className={cn(text.muted, 'block text-[10px] tabular-nums')}>{room.requirements_selected} of {room.requirements_total}</span>
                  </span>
                  <span className="flex-1 min-w-0">
                    {widest ? (
                      <>
                        <span className="block text-[12px] font-semibold text-amber-400 truncate">{widest.requirement}</span>
                        <span className={cn(text.muted, 'block text-[10.5px] tabular-nums truncate')}>
                          {money(widest.candidate_price_spread!.min)}–{money(widest.candidate_price_spread!.max)}
                        </span>
                      </>
                    ) : (
                      <span className={cn(text.muted, 'text-[12px]')}>nothing undecided</span>
                    )}
                  </span>
                  <span className="w-[88px] flex-none text-right font-mono text-[13px] font-bold tabular-nums text-amber-400">
                    {spread != null ? money(spread) : '—'}
                    {spread != null && <small className="block font-normal text-[9.5px] tracking-wide text-muted-foreground">WIDEST SWING</small>}
                  </span>
                </button>

                {expanded && (
                  <div className="px-4 pb-4 pt-1 bg-muted/10 border-t border-border flex flex-col gap-3">
                    {/* Narrative first — the interpretive payoff, not buried under the facts */}
                    {room.insight && (
                      <div>
                        <div className={cn(text.eyebrow, 'mb-1')}>room_story · generated</div>
                        <p className={cn(text.body, 'text-[12px] leading-relaxed')}>{room.insight}</p>
                      </div>
                    )}

                    <div className="flex gap-8 flex-wrap pt-2 border-t border-border">
                      {room.tier_totals.budget != null && (
                        <div className="flex-1 min-w-[220px]">
                          <div className={cn(text.eyebrow, 'mb-1.5')}>Tier totals — furnishing every requirement at that tier</div>
                          <div className="flex items-baseline gap-3">
                            <span className="font-mono text-[13px] tabular-nums text-muted-foreground">{money(room.tier_totals.budget)}</span>
                            <span className="font-mono text-[15px] font-bold tabular-nums text-foreground">{money(room.tier_totals.mid!)}</span>
                            <span className="font-mono text-[13px] tabular-nums text-muted-foreground">{money(room.tier_totals.premium!)}</span>
                          </div>
                          <div className={cn(text.muted, 'text-[10.5px] mt-0.5')}>budget · mid · premium</div>
                        </div>
                      )}
                      {undecided.length > 0 && (
                        <div className="flex-1 min-w-[220px]">
                          <div className={cn(text.eyebrow, 'mb-1.5')}>Undecided, by spread ({undecided.length})</div>
                          <div className="flex flex-col gap-1">
                            {undecided.slice(0, UNDECIDED_PREVIEW_COUNT).map((u) => (
                              <div key={u.req} className="flex gap-2 items-baseline text-[12px]">
                                <span className={cn(text.muted, 'flex-1 truncate')}>{u.req}</span>
                                <span className="font-mono font-semibold tabular-nums text-amber-400 flex-none">{money(u.spread)}</span>
                              </div>
                            ))}
                            {undecided.length > UNDECIDED_PREVIEW_COUNT && (
                              <div className={cn(text.muted, 'text-[10.5px]')}>+{undecided.length - UNDECIDED_PREVIEW_COUNT} more</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
